import axios from 'axios';

// Set default base URL for API calls
axios.defaults.baseURL = window.location.origin;

// Define the API URL for Data API Builder (configurable via env)
export const API_URL = (process.env.REACT_APP_DAB_BASE_URL && process.env.REACT_APP_DAB_BASE_URL.trim())
    ? process.env.REACT_APP_DAB_BASE_URL.trim().replace(/\/+$/, '')
    : '/data-api/rest';

/**
 * Check if API_URL points to external DAB (Container Apps) vs local (/data-api/rest)
 */
const isExternalDab = (): boolean => {
    return API_URL.startsWith('http://') || API_URL.startsWith('https://');
};

/**
 * List of DAB endpoints that require authentication (don't have anonymous access)
 * Endpoints NOT in this list can be called directly even with external DAB
 */
const AUTHENTICATED_ENDPOINTS = [
    'TravelForms',
    'TravelAccommodations',
    'TravelFormCountries',
    'TravelSegments',
    'TravelCalendar',
    'TravelOverview',
    'PPDForms',
    'PPDCompetencies',
    'PPDFormCompetencies',
    'PPDPending',
    'PPDWithCompetencies',
    'BusinessCases',
    'BusinessCaseDrafts',
    'EventForms',
    'IncidentReports',
    'CrisisLogs',
    'GetTravel',
    'UpdateTravel',
    'DeleteTravel',
    'DeleteFormChildren'
];

/**
 * Check if an endpoint requires authentication
 * @param endpointPath - The endpoint path (e.g., "TravelForms" or "TravelForms/123")
 */
const requiresAuthentication = (endpointPath: string): boolean => {
    // Extract base endpoint name (before first / or ?)
    const baseEndpoint = endpointPath.split('/')[0].split('?')[0];
    return AUTHENTICATED_ENDPOINTS.some(authEndpoint =>
        baseEndpoint === authEndpoint || baseEndpoint.startsWith(authEndpoint + '/')
    );
};

/**
 * Get the URL to use for a DAB API call
 * Routes authenticated requests through proxy when using external DAB
 * @param endpointPath - The DAB endpoint path (e.g., "TravelForms" or "TravelForms?id=123")
 * @param needsAuth - Whether this specific call requires authentication (default: true)
 * @returns The full URL to call
 */
export const getDabUrl = (endpointPath: string, needsAuth: boolean = true): string => {
    // If using local DAB (/data-api/rest), always use direct
    if (!isExternalDab()) {
        return `${API_URL}/${endpointPath}`;
    }

    // For external DAB:
    // - If endpoint requires auth, route through proxy
    // - If endpoint is anonymous (like CVContent), use direct
    if (needsAuth && requiresAuthentication(endpointPath)) {
        // Route through proxy: /api/dab-proxy?path=TravelForms
        const queryParams = new URLSearchParams();
        queryParams.append('path', endpointPath);
        return `/api/dab-proxy?${queryParams.toString()}`;
    }

    // Direct call for anonymous endpoints
    return `${API_URL}/${endpointPath}`;
};

// --- Constants for Language Proximity Search ---
const LANGUAGES = new Set([
    "english", "french", "spanish", "german", "portuguese", "arabic", "russian", "mandarin", "hindi", "swahili", "italian",
    "bengali", "urdu", "punjabi", "turkish", "japanese", "korean", "vietnamese", "thai", "dutch", "greek", "polish",
    "romanian", "czech", "hungarian", "hebrew", "malay", "indonesian", "filipino", "tamil", "telugu", "kannada", "marathi",
    "gujarati", "sinhala", "burmese", "khmer", "lao", "nepali", "pashto", "dari", "kurdish", "somali", "amharic", "yoruba",
    "hausa", "igbo", "zulu", "afrikaans", "serbian", "croatian", "bosnian", "slovak", "slovenian", "bulgarian", "finnish",
    "swedish", "norwegian", "danish", "ukrainian", "georgian", "armenian", "farsi", "azerbaijani", "uzbek", "kazakh",
    "mongolian", "tibetan", "american sign language", "british sign language", "esperanto", "latin"
]); // Comprehensive language list - all lowercase for proper matching
const LANGUAGE_PROXIES = new Set(['fluent', 'proficient', 'basic', 'intermediate', 'native', 'working knowledge', 'language', 'languages', 'speak', 'read', 'write', 'A1', 'A2', 'B1', 'B2', 'C1', 'C2']); // Add more as needed, keep lowercase
const PROXIMITY_CHARS = 50; // How close language term needs to be to proxy word (character distance)

// Track progress callbacks
const progressCallbacks: { [key: string]: ((progress: number) => void) | undefined } = {
    cv: undefined,
    proposal: undefined
};

// Type definitions for search results
interface BackendSearchResult {
    id: string;
    filename?: string;
    title?: string;
    displayName?: string;
    source?: string;
    type?: string;
    totalHits?: number;
    relevanceScore?: number;
    keywordHits?: { [key: string]: number };
    indexedDate?: string;
    // Additional fields for expandable tabs
    termMatches?: any[];
    hitCounts?: { [key: string]: number };
    searchTerms?: string[];
    isStrictAnd?: boolean;
    termsWithHits?: string[];
}

interface BackendSearchResponse {
    results: BackendSearchResult[];
    duration?: number;
}

/**
 * Search CVs using Data API Builder with proper Boolean logic and accurate hit counting
 * 
 * @param query - The search query (supports AND, OR, "quoted phrases")
 * @param onProgress - Optional callback for progress updates
 * @param useMixedHits - Whether to use mixed hits sorting algorithm
 * @returns Search results with accurate hit counts
 */
export const searchCVs = async (query: string, onProgress?: (progress: number) => void, useMixedHits: boolean = false) => {
    try {
        if (onProgress) {
            progressCallbacks['cv'] = onProgress;
            // Use requestAnimationFrame to batch progress updates
            requestAnimationFrame(() => onProgress(25));
        }

        console.log(`­ƒöì Searching CVs with Data API Builder: "${query}", mixed hits: ${useMixedHits}`);

        // Parse the search query for Boolean logic
        const queryParts = parseSearchQuery(query);
        console.log('­ƒöº Parsed query:', queryParts);

        // CRITICAL: Use Azure Data API Builder's built-in pagination (no OData parameters)
        console.log('­ƒöì Fetching ALL CVs using Azure Data API Builder pagination...');
        let allResults = [];
        let nextUrl = `${API_URL}/CVContent`; // Start with basic endpoint
        let pageCount = 0;
        let totalExpected = 1500; // Estimate based on your dataset

        // Follow nextLink pagination until we have all records
        while (nextUrl) {
            try {
                pageCount++;
                console.log(`­ƒôí Fetching page ${pageCount}: ${nextUrl}`);

                const response = await axios.get(nextUrl, {
                    timeout: 15000,
                });

                const data = response.data;
                const pageResults = data.value || [];

                console.log(`Ô£à Page ${pageCount}: Got ${pageResults.length} CVs (Total so far: ${allResults.length + pageResults.length})`);
                allResults.push(...pageResults);

                // Check for nextLink to continue pagination
                nextUrl = data['@odata.nextLink'] || data.nextLink || null;

                // Normalize nextLink to our configured API base (handles absolute + relative)
                if (nextUrl) {
                    try {
                        // Always use https
                        nextUrl = nextUrl.replace(/^http:\/\//i, 'https://');

                        const apiBase = API_URL.replace(/\/+$/, ''); // e.g. '/data-api/rest'

                        // Use URL parsing against current origin to handle absolute/relative links
                        const parsed = new URL(nextUrl, window.location.origin);

                        // If nextLink path is native DAB '/rest/...', rewrite to our API base
                        if (parsed.pathname.startsWith('/rest/')) {
                            const restSuffix = parsed.pathname.substring('/rest'.length); // '/CVContent...'
                            nextUrl = `${apiBase}${restSuffix}${parsed.search || ''}`;
                        }
                        // If nextLink is already under '/data-api/rest', normalize host/base to API_URL
                        else if (parsed.pathname.startsWith('/data-api/rest/')) {
                            const dataApiSuffix = parsed.pathname.substring('/data-api/rest'.length);
                            nextUrl = `${apiBase}${dataApiSuffix}${parsed.search || ''}`;
                        }
                        // Relative path variant like 'rest/...'
                        else if (nextUrl.startsWith('rest/')) {
                            nextUrl = `${apiBase}/${nextUrl.substring('rest/'.length)}`;
                        }
                    } catch (e) {
                        // If parsing fails, leave nextUrl as-is; first page still loaded
                    }

                    console.log(`­ƒöù Next page URL: ${nextUrl}`);
                } else {
                    console.log(`­ƒÅü No more pages - pagination complete with ${allResults.length} total CVs`);
                }

                // Update progress based on actual data received with batched updates
                if (onProgress) {
                    const progress = Math.min(25 + (allResults.length / totalExpected) * 25, 50);
                    // Use requestAnimationFrame to batch progress updates and reduce forced reflows
                    requestAnimationFrame(() => {
                        onProgress(progress);
                    });
                }

                // Safety check to prevent infinite loops
                if (pageCount > 50) {
                    console.warn(`ÔÜá´©Å Safety break: Reached ${pageCount} pages, stopping pagination`);
                    break;
                }

            } catch (error) {
                console.error(`ÔØî Failed to fetch page ${pageCount}:`, error);

                // If we have some results, continue with what we have
                if (allResults.length > 0) {
                    console.log(`ÔÜá´©Å Continuing with ${allResults.length} CVs from previous pages`);
                    break;
                } else {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                    throw new Error(`Failed to fetch any CV data: ${errorMessage}`);
                }
            }
        }

        // Validate dataset size
        console.log(`­ƒôè Final dataset: ${allResults.length} CVs loaded`);
        if (allResults.length < 50) {
            console.warn(`ÔÜá´©Å Warning: Only ${allResults.length} CVs loaded, expected around ${totalExpected}`);
        }

        // Enhanced progress update
        if (onProgress) {
            requestAnimationFrame(() => onProgress(50));
        }

        // **PROCESS ALL CVS WITH DETAILED SEARCH LOGIC**
        console.log('­ƒöì Starting detailed client-side search...');
        console.log(`­ƒöº Search query: "${query}", Parsed:`, queryParts);
        console.log(`­ƒôï Processing all ${allResults.length} CVs with detailed Boolean search`);

        if (onProgress) {
            requestAnimationFrame(() => onProgress(75));
        }

        // **PROCESS ALL CVS WITH DETAILED MATCHING AND HIT COUNTING**
        const processedResults = [];

        for (const cv of allResults) {
            if (!cv.content) continue;

            // Apply Boolean logic and calculate accurate hit counts for ranking
            const searchResult = evaluateDocumentMatch(cv, queryParts);
            if (searchResult.matches) {
                // Extract metadata on-the-fly from filename
                const metadata = extractMetadataFromFilename(cv.filename || '');

                const result = {
                    id: cv.id,
                    filename: cv.filename,
                    // Use extracted metadata for display
                    folderName: metadata.folder_name || 'Unknown Folder',
                    associate_name: metadata.associate_name,
                    source_location: cv.filename, // Ô£à Use original filename for blob storage lookup
                    pdf_filename: cv.filename, // Ô£à Use original filename for blob storage lookup
                    pdfFilename: cv.filename, // Ô£à Use original filename for blob storage lookup
                    displayName: metadata.associate_name || metadata.pdf_filename?.replace(/\.pdf$/i, '') || `Document ${cv.id}`,
                    title: metadata.associate_name || metadata.pdf_filename?.replace(/\.pdf$/i, '') || `Document ${cv.id}`,
                    source: 'cv',
                    type: 'cv',
                    totalHits: searchResult.totalHits,
                    relevanceScore: searchResult.totalHits,
                    // Ô£à CRITICAL: Include all fields needed for expandable tabs
                    termMatches: searchResult.termMatches || [],
                    hitCounts: searchResult.hitCounts || {},
                    searchTerms: searchResult.searchTerms || queryParts.terms || [],
                    isStrictAnd: queryParts.operator === 'AND' || queryParts.complexBoolean,
                    termsWithHits: searchResult.termsWithHits || []
                };

                processedResults.push(result);
            }
        }

        console.log(`­ƒÄ» Found ${processedResults.length} matching CVs out of ${allResults.length} total CVs`);

        // Sort results based on mixed hits or total hits
        let sortedResults;
        if (useMixedHits && queryParts.terms.length > 1) {
            // Mixed hits: prefer balanced distribution across terms
            sortedResults = processedResults.sort((a, b) => {
                const aBalance = calculateMixedHitsScore(a.termMatches);
                const bBalance = calculateMixedHitsScore(b.termMatches);
                return bBalance - aBalance;
            });
            console.log('­ƒôê Sorted by mixed hits score for balanced term distribution');
        } else {
            // Standard: sort by total hits
            sortedResults = processedResults.sort((a, b) => b.totalHits - a.totalHits);
            console.log('­ƒôê Sorted by total hits');
        }

        if (onProgress) {
            requestAnimationFrame(() => onProgress(100));
        }

        console.log(`Ô£à Found ${sortedResults.length} matching CVs using client-side Boolean search with proper pagination`);
        return sortedResults;

    } catch (error: any) {
        console.error('ÔØî Error searching CVs with Data API Builder:', error);
        if (onProgress) {
            onProgress(0);
        }

        // Provide more specific error information for pagination issues
        if (error.response?.status === 400) {
            const errorMessage = error.response?.data?.message || error.message;
            if (errorMessage.includes('$top') || errorMessage.includes('pagination')) {
                throw new Error(`Pagination error: ${errorMessage}. The page size may be too large.`);
            }
        }

        throw error;
    }
};

/**
 * Search proposals using the backend SearchFunction API (vector search)
 * 
 * @param query - The search query
 * @param onProgress - Optional callback for progress updates
 * @param useMixedHits - Whether to use mixed hits sorting algorithm
 * @returns Search results
 */
export const searchProposals = async (query: string, onProgress?: (progress: number) => void, useMixedHits: boolean = false) => {
    try {
        if (onProgress) {
            progressCallbacks['proposal'] = onProgress;
            requestAnimationFrame(() => onProgress(25));
        }

        console.log(`­ƒöì Searching proposals with backend API: "${query}"`);

        if (onProgress) {
            requestAnimationFrame(() => onProgress(50));
        }

        // Ô£à Use Backend API for proposal search (vector search)
        const response = await axios.post<BackendSearchResponse>('/api/SearchFunction', {
            query: query,
            searchType: 'proposals',
            useMixedHits: useMixedHits
        }, {
            timeout: 60000, // 60 second timeout for proposal search
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (onProgress) {
            requestAnimationFrame(() => onProgress(75));
        }

        const searchResults: any[] = response.data.results || [];
        console.log(`­ƒôï Backend returned ${searchResults.length} proposal results`);

        // Ô£à Transform results while preserving the full data structure
        const transformedResults = searchResults
            .filter((result: any) => result.source === 'proposal' || result.type === 'proposal')
            .map((result: any) => ({
                // Preserve all the important fields for expandable tabs
                id: result.id,
                filename: result.filename,
                displayName: result.displayName || result.filename?.replace(/\.pdf$/i, '') || 'Unknown Document',
                title: result.title || result.displayName || result.filename,
                source: result.source,
                totalHits: result.totalHits || result.relevanceScore || 0,
                relevanceScore: result.relevanceScore || result.totalHits || 0,
                // Ô£à CRITICAL: Preserve termMatches for expandable tabs
                termMatches: result.termMatches || [],
                // Keep additional metadata
                hitCounts: result.hitCounts,
                searchTerms: result.searchTerms,
                isStrictAnd: result.isStrictAnd,
                termsWithHits: result.termsWithHits
            }));

        if (onProgress) {
            requestAnimationFrame(() => onProgress(100));
        }

        console.log(`Ô£à Found ${transformedResults.length} matching proposals with termMatches preserved`);
        console.log('­ƒôè Sample result structure:', transformedResults[0] ? {
            id: transformedResults[0].id,
            displayName: transformedResults[0].displayName,
            totalHits: transformedResults[0].totalHits,
            termMatchesCount: transformedResults[0].termMatches?.length || 0
        } : 'No results');

        return transformedResults;

    } catch (error: any) {
        console.error('ÔØî Error searching proposals:', error);
        if (onProgress) {
            onProgress(0);
        }
        throw error;
    }
};

/**
 * Parse search query into terms, operators, and quoted phrases
 * Supports complex boolean expressions with proper precedence
 */
function parseSearchQuery(query: string) {
    console.log(`­ƒöº Original query: "${query}"`);

    const result = {
        terms: [] as string[],
        operator: 'AND' as 'AND' | 'OR',
        quotedPhrases: [] as string[],
        originalQuery: query.trim(),
        complexBoolean: false,
        groups: [] as any[] // For complex boolean expressions
    };

    // Extract quoted phrases first and clean them
    const phrases: string[] = [];
    let processedQuery = query.replace(/"([^"]+)"/g, (match, phrase) => {
        const cleanPhrase = phrase.trim().toLowerCase();
        console.log(`­ƒöº Extracted quoted phrase: "${cleanPhrase}"`);
        phrases.push(cleanPhrase);
        return `__PHRASE_${phrases.length - 1}__`;
    });

    result.quotedPhrases = phrases;
    console.log(`­ƒöº Processed query after phrase extraction: "${processedQuery}"`);
    console.log(`­ƒöº Extracted phrases:`, phrases);

    // Check for complex boolean expressions (mixed AND/OR)
    const hasAnd = processedQuery.toUpperCase().includes(' AND ');
    const hasOr = processedQuery.toUpperCase().includes(' OR ');

    if (hasAnd && hasOr) {
        // Complex boolean expression - handle with proper precedence
        console.log(`­ƒöº Detected complex boolean expression with mixed AND/OR`);
        result.complexBoolean = true;
        result.groups = parseComplexBoolean(processedQuery);
        console.log(`­ƒöº Parsed complex boolean groups:`, result.groups);
        return result;
    }

    // Helper function to safely convert terms to lowercase while preserving placeholders
    const safeToLowerCase = (term: string): string => {
        if (term.match(/^__PHRASE_\d+__$/i)) {
            // Keep placeholder as-is to maintain case for matching
            return term.toUpperCase(); // Ensure consistent uppercase for placeholders
        }
        return term.toLowerCase();
    };

    // Simple boolean expressions (only AND or only OR)
    if (processedQuery.toUpperCase().includes(' OR ')) {
        result.operator = 'OR';
        result.terms = processedQuery.split(/\s+OR\s+/i)
            .map(term => safeToLowerCase(term.trim()))
            .filter(t => t);
    } else if (processedQuery.toUpperCase().includes(' AND ')) {
        result.operator = 'AND';
        result.terms = processedQuery.split(/\s+AND\s+/i)
            .map(term => safeToLowerCase(term.trim()))
            .filter(t => t);
    } else {
        // No explicit operator - treat as AND for multi-word, single term otherwise
        const words = processedQuery.trim().split(/\s+/).filter(w => w);
        if (words.length > 1 && phrases.length === 0) {
            // Multiple words but no phrases - treat each word as separate term with AND
            result.operator = 'AND';
            result.terms = words.map(w => safeToLowerCase(w));
        } else if (words.length === 1 && phrases.length > 0) {
            // Single word/placeholder with phrases - just the one term
            result.terms = [safeToLowerCase(processedQuery.trim())].filter(t => t);
        } else if (words.length > 1 && phrases.length > 0) {
            // Mixed: phrases and individual words - treat as AND
            result.operator = 'AND';
            result.terms = words.map(w => safeToLowerCase(w));
        } else {
            // Single term or phrase only
            result.terms = [safeToLowerCase(processedQuery.trim())].filter(t => t);
        }
    }

    console.log(`­ƒöº Final parsed result:`, result);
    return result;
}

/**
 * Parse complex boolean expressions with user-expected precedence
 * For search interfaces, users typically expect: "A AND B OR C" to mean "A AND (B OR C)"
 * This implements search-engine-style precedence where the first term applies to the entire expression
 */
function parseComplexBoolean(query: string) {
    console.log(`­ƒöº Parsing complex boolean: "${query}"`);

    // For mixed AND/OR expressions, use search-engine-style precedence:
    // "A AND B OR C" becomes "A AND (B OR C)"

    // First, find the first AND operator
    const firstAndMatch = query.match(/^(.+?)\s+AND\s+(.+)$/i);

    if (firstAndMatch) {
        const [, firstTerm, restOfQuery] = firstAndMatch;

        console.log(`­ƒöº First term: "${firstTerm.trim()}", Rest: "${restOfQuery.trim()}"`);

        // Check if the rest contains OR
        if (restOfQuery.toUpperCase().includes(' OR ')) {
            // Parse the rest as OR terms  
            const orTerms = restOfQuery.split(/\s+OR\s+/i)
                .map(term => term.trim().toLowerCase())
                .filter(t => t);

            console.log(`­ƒöº Search-style parsing: "${firstTerm.trim()}" AND (${orTerms.join(' OR ')})`);

            // Create structure that the evaluation logic can handle
            // We'll return a special structure indicating this pattern
            return [{
                operator: 'SEARCH_STYLE', // Special operator to indicate this parsing style
                requiredTerm: firstTerm.trim().toLowerCase(),
                orTerms: orTerms
            }];
        }
    }

    // Fallback to original logic for other patterns
    const orParts = query.split(/\s+OR\s+/i);

    const groups = orParts.map(orPart => {
        const andTerms = orPart.split(/\s+AND\s+/i)
            .map(term => term.trim().toLowerCase())
            .filter(t => t);

        return {
            operator: 'AND',
            terms: andTerms
        };
    });

    console.log(`­ƒöº Complex boolean groups (fallback):`, groups);
    return groups;
}

/**
 * Extract context snippets around search terms for expandable tabs
 * @param content - The document content
 * @param term - The search term
 * @param maxSnippets - Maximum number of snippets to return
 * @returns Array of context snippets
 */
function extractContextSnippets(content: string, term: string, maxSnippets: number = 10): string[] {
    const snippets: string[] = [];
    const termLower = term.toLowerCase();
    const contentLower = content.toLowerCase();
    const contextLength = 150; // Characters before and after the term

    let pos = 0;
    let count = 0;

    while ((pos = contentLower.indexOf(termLower, pos)) !== -1 && count < maxSnippets) {
        const start = Math.max(0, pos - contextLength);
        const end = Math.min(content.length, pos + termLower.length + contextLength);

        let snippet = content.substring(start, end).trim();

        // Add ellipsis if we're not at the beginning/end
        if (start > 0) snippet = '...' + snippet;
        if (end < content.length) snippet = snippet + '...';

        // Highlight the search term (case-insensitive)
        const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        snippet = snippet.replace(regex, '**$1**');

        snippets.push(snippet);
        pos += termLower.length;
        count++;
    }

    return snippets;
}

/**
 * Extract context snippets that respect language proximity logic
 * Only shows snippets for valid matches (language terms near proficiency indicators)
 * @param content - The document content
 * @param term - The search term
 * @param maxSnippets - Maximum number of snippets to return
 * @returns Array of context snippets for valid matches only
 */
function extractValidContextSnippets(content: string, term: string, maxSnippets: number = 10): string[] {
    const snippets: string[] = [];
    const termLower = term.toLowerCase();
    const contentLower = content.toLowerCase();
    const contextLength = 150; // Characters before and after the term

    if (!LANGUAGES.has(termLower)) {
        // Not a language term, use regular context extraction
        return extractContextSnippets(content, term, maxSnippets);
    }

    // This is a language term - only extract contexts for valid proximity matches
    let pos = 0;
    let count = 0;

    while ((pos = contentLower.indexOf(termLower, pos)) !== -1 && count < maxSnippets) {
        // Check if this occurrence has valid proximity to language indicators
        let foundProxy = false;

        for (const proxy of Array.from(LANGUAGE_PROXIES)) {
            // Check if a proxy term is within PROXIMITY_CHARS characters
            const startPos = Math.max(0, pos - PROXIMITY_CHARS);
            const endPos = Math.min(contentLower.length, pos + termLower.length + PROXIMITY_CHARS);
            const context = contentLower.substring(startPos, endPos);

            if (context.includes(proxy)) {
                foundProxy = true;
                console.log(`­ƒîÉ Valid context: "${termLower}" found near "${proxy}"`);
                break;
            }
        }

        if (foundProxy) {
            // This is a valid match - extract context snippet
            const start = Math.max(0, pos - contextLength);
            const end = Math.min(content.length, pos + termLower.length + contextLength);

            let snippet = content.substring(start, end).trim();

            // Add ellipsis if we're not at the beginning/end
            if (start > 0) snippet = '...' + snippet;
            if (end < content.length) snippet = snippet + '...';

            // Highlight the search term (case-insensitive)
            const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
            snippet = snippet.replace(regex, '**$1**');

            snippets.push(snippet);
            count++;
        }

        pos += termLower.length;
    }

    return snippets;
}

/**
 * Check if a language term appears with proper proximity to language proxy words
 * @param content - The document content (lowercase)
 * @param term - The term to search for
 * @returns Number of valid occurrences (with proximity for language terms)
 */
function countTermWithLanguageProximity(content: string, term: string): number {
    const termLower = term.toLowerCase();

    if (!LANGUAGES.has(termLower)) {
        // Not a language term, use regular counting
        return (content.match(new RegExp(term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')) || []).length;
    }

    // This is a language term - apply proximity logic
    let count = 0;
    let pos = 0;

    while ((pos = content.indexOf(termLower, pos)) !== -1) {
        // Check if the language is near a language proxy term
        let foundProxy = false;

        for (const proxy of Array.from(LANGUAGE_PROXIES)) {
            // Check if a proxy term is within PROXIMITY_CHARS characters
            const startPos = Math.max(0, pos - PROXIMITY_CHARS);
            const endPos = Math.min(content.length, pos + termLower.length + PROXIMITY_CHARS);
            const context = content.substring(startPos, endPos);

            if (context.includes(proxy)) {
                foundProxy = true;
                console.log(`­ƒîÉ Language proximity match: "${termLower}" found near "${proxy}"`);
                break;
            }
        }

        if (foundProxy) {
            count++;
        }

        pos += termLower.length;
    }

    return count;
}

/**
 * Evaluate if a document matches the search criteria and calculate hit counts
 */
function evaluateDocumentMatch(document: any, queryParts: any) {
    const content = document.content.toLowerCase();
    let termMatches: any[] = [];
    let totalHits = 0;
    let hitCounts: { [key: string]: number } = {};
    let termsWithHits: string[] = [];

    // Handle complex boolean expressions
    if (queryParts.complexBoolean && queryParts.groups.length > 0) {
        console.log(`­ƒôï Evaluating complex boolean expression with ${queryParts.groups.length} groups`);

        let documentMatches = false;

        // Process each OR group
        for (const group of queryParts.groups) {
            // Handle search-style boolean: "A AND (B OR C)"
            if (group.operator === 'SEARCH_STYLE') {
                console.log(`­ƒôï Processing SEARCH_STYLE: "${group.requiredTerm}" AND (${group.orTerms.join(' OR ')})`);

                let requiredTermActual = group.requiredTerm;
                let requiredIsPhrase = false;

                // Check if required term is a placeholder for a quoted phrase
                if (group.requiredTerm.match(/^__phrase_\d+__$/i)) {
                    const phraseMatch = group.requiredTerm.match(/^__phrase_(\d+)__$/i);
                    const phraseIndex = phraseMatch ? parseInt(phraseMatch[1]) : 0;
                    requiredTermActual = queryParts.quotedPhrases[phraseIndex] || group.requiredTerm;
                    requiredIsPhrase = true;
                    console.log(`­ƒöº Required term is phrase: "${requiredTermActual}"`);
                }

                // The required term must be present
                const requiredTermCount = countTermWithLanguageProximity(content, requiredTermActual);
                console.log(`­ƒöì SEARCH_STYLE - Required term "${requiredTermActual}" found ${requiredTermCount} times`);

                if (requiredTermCount === 0) {
                    console.log(`ÔØî SEARCH_STYLE: Required term "${requiredTermActual}" not found, skipping`);
                    continue; // Skip this group if required term not found
                }

                // At least one of the OR terms must be present
                let orTermFound = false;
                const searchStyleTermMatches: any[] = [];
                let searchStyleTotalHits = 0;

                // First add the required term
                const requiredContextSnippets = extractValidContextSnippets(document.content, requiredTermActual, 10);
                searchStyleTermMatches.push({
                    term: requiredTermActual,
                    hitCount: requiredTermCount,
                    isPhrase: requiredIsPhrase,
                    matchType: requiredIsPhrase ? 'exact phrase' : 'keyword',
                    matches: requiredContextSnippets.map(context => ({ context, term: requiredTermActual }))
                });
                searchStyleTotalHits += requiredTermCount;
                hitCounts[requiredTermActual] = requiredTermCount;
                termsWithHits.push(requiredTermActual);

                // Now check OR terms
                for (const orTerm of group.orTerms) {
                    let orTermActual = orTerm;
                    let orIsPhrase = false;

                    // Check if OR term is a placeholder for a quoted phrase
                    if (orTerm.match(/^__phrase_\d+__$/i)) {
                        const phraseMatch = orTerm.match(/^__phrase_(\d+)__$/i);
                        const phraseIndex = phraseMatch ? parseInt(phraseMatch[1]) : 0;
                        orTermActual = queryParts.quotedPhrases[phraseIndex] || orTerm;
                        orIsPhrase = true;
                        console.log(`­ƒöº OR term is phrase: "${orTermActual}"`);
                    }

                    const orTermCount = countTermWithLanguageProximity(content, orTermActual);
                    console.log(`­ƒöì SEARCH_STYLE - OR term "${orTermActual}" found ${orTermCount} times`);

                    if (orTermCount > 0) {
                        orTermFound = true;
                        const orContextSnippets = extractValidContextSnippets(document.content, orTermActual, 10);
                        searchStyleTermMatches.push({
                            term: orTermActual,
                            hitCount: orTermCount,
                            isPhrase: orIsPhrase,
                            matchType: orIsPhrase ? 'exact phrase' : 'keyword',
                            matches: orContextSnippets.map(context => ({ context, term: orTermActual }))
                        });
                        searchStyleTotalHits += orTermCount;
                        hitCounts[orTermActual] = orTermCount;
                        termsWithHits.push(orTermActual);
                    }
                }

                if (orTermFound) {
                    console.log(`Ô£à SEARCH_STYLE: Required term "${requiredTermActual}" found AND at least one OR term found, document accepted`);
                    documentMatches = true;
                    termMatches.push(...searchStyleTermMatches);
                    totalHits += searchStyleTotalHits;
                } else {
                    console.log(`ÔØî SEARCH_STYLE: Required term found but no OR terms found, document rejected`);
                }

                continue; // Move to next group
            }

            // Handle regular AND groups
            let groupMatches = true;
            const groupTermMatches: any[] = [];
            let groupTotalHits = 0;

            console.log(`­ƒôï Processing AND group:`, group.terms);

            // All terms in a group must match (AND logic within group)
            for (const term of group.terms) {
                let actualTerm = term;
                let isPhrase = false;

                // Check if this is a placeholder for a quoted phrase
                if (term.match(/^__phrase_\d+__$/i)) {
                    const phraseMatch = term.match(/^__phrase_(\d+)__$/i);
                    const phraseIndex = phraseMatch ? parseInt(phraseMatch[1]) : 0;
                    actualTerm = queryParts.quotedPhrases[phraseIndex] || term;
                    isPhrase = true;
                }

                // Count actual occurrences with language proximity logic
                const termOccurrences = countTermWithLanguageProximity(content, actualTerm);
                console.log(`­ƒöì Term "${actualTerm}" found ${termOccurrences} times`);

                if (termOccurrences > 0) {
                    const contextSnippets = extractValidContextSnippets(document.content, actualTerm, 10);
                    groupTermMatches.push({
                        term: actualTerm,
                        hitCount: termOccurrences,
                        isPhrase: isPhrase,
                        matchType: isPhrase ? 'exact phrase' : 'keyword',
                        matches: contextSnippets.map(context => ({ context, term: actualTerm })) // Frontend expects matches array with context objects
                    });
                    groupTotalHits += termOccurrences;
                    hitCounts[actualTerm] = termOccurrences;
                    termsWithHits.push(actualTerm);
                } else {
                    // For AND within group: if any term is missing, group doesn't match
                    groupMatches = false;
                    console.log(`ÔØî Complex AND logic: Term "${actualTerm}" not found in group, group rejected`);
                    break;
                }
            }

            if (groupMatches) {
                documentMatches = true;
                termMatches.push(...groupTermMatches);
                totalHits += groupTotalHits;
            }
        }
    } else {
        // Handle simple boolean expressions
        console.log(`­ƒöº Simple boolean: ${queryParts.operator} with ${queryParts.terms.length} terms`);

        if (queryParts.operator === 'AND') {
            // For AND: ALL terms must be present
            let allTermsFound = true;
            const tempTermMatches: any[] = [];
            const tempHitCounts: { [key: string]: number } = {};
            const tempTermsWithHits: string[] = [];
            let tempTotalHits = 0;

            for (const term of queryParts.terms) {
                let actualTerm = term;
                let isPhrase = false;

                // Check if this is a placeholder for a quoted phrase
                if (term.match(/^__phrase_\d+__$/i)) {
                    const phraseMatch = term.match(/^__phrase_(\d+)__$/i);
                    const phraseIndex = phraseMatch ? parseInt(phraseMatch[1]) : 0;
                    actualTerm = queryParts.quotedPhrases[phraseIndex] || term;
                    isPhrase = true;
                }

                // Count actual occurrences with language proximity logic
                const termOccurrences = countTermWithLanguageProximity(content, actualTerm);
                console.log(`­ƒöì Term "${actualTerm}" found ${termOccurrences} times`);

                if (termOccurrences > 0) {
                    const contextSnippets = extractValidContextSnippets(document.content, actualTerm, 10);
                    tempTermMatches.push({
                        term: actualTerm,
                        hitCount: termOccurrences,
                        isPhrase: isPhrase,
                        matchType: isPhrase ? 'exact phrase' : 'keyword',
                        matches: contextSnippets.map(context => ({ context, term: actualTerm })) // Frontend expects matches array with context objects
                    });
                    tempTotalHits += termOccurrences;
                    tempHitCounts[actualTerm] = termOccurrences;
                    tempTermsWithHits.push(actualTerm);
                } else {
                    // For AND: if any term is missing, document doesn't match
                    allTermsFound = false;
                    console.log(`ÔØî AND logic: Term "${actualTerm}" not found, document rejected`);
                    break;
                }
            }

            // Only add results if ALL terms were found (strict AND)
            if (allTermsFound) {
                console.log(`Ô£à AND logic: All ${queryParts.terms.length} terms found, document accepted`);
                termMatches = tempTermMatches;
                totalHits = tempTotalHits;
                hitCounts = tempHitCounts;
                termsWithHits = tempTermsWithHits;
            } else {
                console.log(`ÔØî AND logic: Not all terms found, document rejected`);
            }
        } else {
            // For OR: ANY term can match
            for (const term of queryParts.terms) {
                let actualTerm = term;
                let isPhrase = false;

                // Check if this is a placeholder for a quoted phrase
                if (term.match(/^__phrase_\d+__$/i)) {
                    const phraseMatch = term.match(/^__phrase_(\d+)__$/i);
                    const phraseIndex = phraseMatch ? parseInt(phraseMatch[1]) : 0;
                    actualTerm = queryParts.quotedPhrases[phraseIndex] || term;
                    isPhrase = true;
                }

                // Count actual occurrences with language proximity logic
                const termOccurrences = countTermWithLanguageProximity(content, actualTerm);
                if (termOccurrences > 0) {
                    const contextSnippets = extractValidContextSnippets(document.content, actualTerm, 10);
                    termMatches.push({
                        term: actualTerm,
                        hitCount: termOccurrences,
                        isPhrase: isPhrase,
                        matchType: isPhrase ? 'exact phrase' : 'keyword',
                        matches: contextSnippets.map(context => ({ context, term: actualTerm })) // Frontend expects matches array with context objects
                    });
                    totalHits += termOccurrences;
                    hitCounts[actualTerm] = termOccurrences;
                    termsWithHits.push(actualTerm);
                }
            }
        }
    }

    return {
        matches: termMatches.length > 0,
        totalHits: totalHits,
        termMatches: termMatches,
        hitCounts: hitCounts,
        termsWithHits: termsWithHits,
        searchTerms: queryParts.terms || []
    };
}

/**
 * Extract metadata from filename
 * 
 * @param filename - The filename to extract metadata from
 * @returns Metadata object
 */
function extractMetadataFromFilename(filename: string) {
    if (!filename) {
        return {
            folder_name: '',
            associate_name: 'Unknown',
            source_location: '',
            pdf_filename: 'Unknown'
        };
    }

    // Split by both forward and backward slashes to handle different path formats
    const pathParts = filename.split(/[\/\\]/);
    const fullFilename = pathParts[pathParts.length - 1]; // Get the last part (filename)
    const filenameParts = fullFilename.split('.');

    const metadata = {
        folder_name: pathParts.slice(0, -1).join('/'),
        associate_name: filenameParts.slice(0, -1).join('.'), // Everything except extension
        source_location: fullFilename,
        pdf_filename: fullFilename
    };

    return metadata;
}

/**
 * Calculate mixed hits score for balanced term distribution
 * Prioritizes documents where all terms are well-represented
 * 
 * @param termMatches - Array of term matches
 * @returns Mixed hits score (higher = better balanced)
 */
function calculateMixedHitsScore(termMatches: any[]) {
    if (!termMatches || termMatches.length === 0) return 0;

    const hitCounts = termMatches.map(tm => tm.hitCount);

    if (hitCounts.length === 1) {
        // Single term - just return hit count
        return hitCounts[0];
    }

    // For balanced distribution, prioritize the minimum hit count
    // This ensures all terms are well-represented
    const minHits = Math.min(...hitCounts);
    const totalHits = hitCounts.reduce((sum, count) => sum + count, 0);

    // Score = (minimum hits * 1000) + total hits
    // This heavily weights balanced distribution while using total as tiebreaker
    // Example: 6+6 hits = (6*1000) + 12 = 6012
    // Example: 100+1 hits = (1*1000) + 101 = 1101
    // The balanced document (6+6) wins!
    const balancedScore = (minHits * 1000) + totalHits;

    console.log(`­ƒôè Mixed hits score: min=${minHits}, total=${totalHits}, score=${balancedScore}`);
    return balancedScore;
}

/**
 * Check system status and database connection
 * 
 * @returns Object containing connection status and system information
 */
export const checkStatus = async () => {
    try {
        console.log('­ƒöì Checking system status and database connection...');

        // Try to get status from the StatusFunction API endpoint
        const response = await axios.get('/api/StatusFunction', {
            timeout: 30000, // Increased from 15000ms to 30000ms for Azure SQL Standard tier
        });

        console.log('Ô£à Status check successful:', response.data);

        // Return the response data with proper structure
        return {
            connectionStatus: {
                connected: true,
                message: 'Database connected successfully',
                timestamp: new Date().toISOString()
            },
            totalFiles: response.data.totalFiles || 0,
            processedFiles: response.data.processedFiles || 0,
            failedFiles: response.data.failedFiles || 0,
            status: response.data.status || 'Idle',
            lastProposalIndexTime: response.data.lastProposalIndexTime || null,
            lastCvIndexTime: response.data.lastCvIndexTime || null
        };

    } catch (error) {
        console.error('ÔØî Status check failed:', error);

        // Return error status
        return {
            connectionStatus: {
                connected: false,
                message: error instanceof Error ? error.message : 'Status check failed',
                timestamp: new Date().toISOString()
            },
            totalFiles: 0,
            processedFiles: 0,
            failedFiles: 0,
            status: 'Error',
            lastProposalIndexTime: null,
            lastCvIndexTime: null
        };
    }
};



// Health/status check helper used by App.tsx

